import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import HeroHomeImage from 'assets/home/herohome.webp';
import HeroHomeMobileImage from 'assets/home/herohomemobile.webp';
import { ROUTES } from 'constants/routes';
import { useIsPatient, useUserStore } from 'stores/user';
import { PaeHeroSessions } from '../PaeHeroSessions';

const HERO_IMG_SHAPE = 'rounded-tl-4xl rounded-br-4xl rounded-tr-none rounded-bl-none';
const HERO_MUTED_TEXT_CLASS = 'text-base leading-5 text-denim-500 am-3xl:text-lg am-3xl:leading-5';
const COPY_STACK_GAP = 'gap-3 am-3xl:gap-6';
const COPY_CTA_EXTRA_MT = 'mt-3 am-3xl:mt-4';

export const PaeHero = (): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.HOME);
  const isPatient = useIsPatient();
  const user = useUserStore(state => state.user);
  const userCanBookAppointments = user?.canBookAppointments === true;

  const handleOnClick = (): void => {
    if (!userCanBookAppointments) {
      navigate(ROUTES.WELL_BEING);
      return;
    }

    if (user && !user.profileCompleteToBookAppointments) {
      navigate(ROUTES.APPOINTMENTS_PERSONAL_DATA);
      return;
    }
    navigate(ROUTES.APPOINTMENTS_RESERVE);
  };

  return (
    <section className="relative overflow-y-visible">
      <div className="absolute top-0 left-1/2 h-114 w-dvw max-w-dvw -translate-x-1/2 bg-mint-100" />
      <div
        className={cn(
          'relative mx-auto grid w-full max-w-7xl grid-cols-1 gap-y-10 px-0 pt-6',
          'am-3xl:grid-cols-2 am-3xl:items-start am-3xl:gap-15 am-3xl:px-15 am-3xl:pt-0'
        )}
      >
        <div
          className={cn(
            'flex min-w-0 w-full flex-col',
            COPY_STACK_GAP,
            'am-3xl:col-start-1 am-3xl:row-start-1 am-3xl:max-w-155 am-3xl:pt-25 am-3xl:pl-15'
          )}
        >
          {!isPatient && <p className={HERO_MUTED_TEXT_CLASS}>{t('Pae.PaeHero.service')}</p>}
          <h2 className="max-w-140 text-[34px] font-medium leading-10 text-denim-800 am-3xl:text-5xl am-3xl:leading-none">
            {userCanBookAppointments
              ? t('Pae.PaeHero.heading')
              : t('Pae.PaeHero.noAppointment.heading')}
          </h2>
          <p className={cn('max-w-155', HERO_MUTED_TEXT_CLASS)}>
            {userCanBookAppointments
              ? t('Pae.PaeHero.description')
              : t('Pae.PaeHero.noAppointment.description')}
          </p>
          <Button
            className={cn(
              COPY_CTA_EXTRA_MT,
              'flex h-12 items-center justify-center gap-2.5 rounded-4xl px-8 py-2 text-center',
              'w-full am-3xl:w-fit am-3xl:shadow-box-lg'
            )}
            onClick={handleOnClick}
          >
            {userCanBookAppointments
              ? t('AppointmentsBanner.cta', { ns: LOCIZE_NS.APPOINTMENTS })
              : t('Pae.PaeHero.noAppointment.cta')}
          </Button>
        </div>
        <div className="flex flex-col gap-4 am-3xl:contents">
          <div
            className={cn(
              'min-w-0 w-full',
              'am-3xl:col-start-2 am-3xl:row-span-2 am-3xl:row-start-1 am-3xl:pt-6'
            )}
          >
            <div
              className={cn(
                'h-49 w-full max-w-full overflow-hidden',
                HERO_IMG_SHAPE,
                'am-md:mx-auto am-md:h-60 am-md:w-fit am-3xl:h-134 am-3xl:w-full'
              )}
            >
              <img
                src={HeroHomeMobileImage}
                alt="Hero home"
                className={cn(
                  'mx-auto h-full w-full max-w-full object-cover',
                  'am-md:w-auto am-3xl:hidden'
                )}
              />
              <img
                src={HeroHomeImage}
                alt="Hero home"
                className={cn('hidden h-full w-full object-cover am-3xl:block')}
              />
            </div>
          </div>
          {userCanBookAppointments && (
            <div className="am-3xl:col-start-1 am-3xl:row-start-2 am-3xl:mt-8 am-3xl:ml-15">
              <PaeHeroSessions />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
